/**
 * AI CONTEXT: legal-compliance - Age verification page
 * 
 * This component implements age verification with:
 * - Mobile-first responsive design
 * - Legal compliance for 21+ requirement
 * - Clear user feedback and instructions
 * - Secure verification process
 * - Touch-friendly interface elements
 * 
 * Architecture decisions:
 * - Use localStorage for verification persistence
 * - Provide clear legal messaging
 * - Implement secure exit mechanisms
 * - Follow mobile-first design principles
 * 
 * Future considerations:
 * - Add biometric age verification
 * - Implement government ID verification
 * - Add multi-language support
 * - Consider advanced verification methods
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';

const AgeVerification = () => {
  const [isVerified, setIsVerified] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const navigate = useNavigate();

  // AI CONTEXT: legal-compliance - Handle age verification
  // Reasoning: Ensure legal compliance with 21+ requirement
  // Constraints: Must verify age before allowing access
  // Future: Implement more secure verification methods

  const handleVerify = () => {
    setIsVerified(true);
    // Store verification in localStorage
    localStorage.setItem('age_verified', 'true');
    // Redirect to home page after verification
    setTimeout(() => {
      navigate('/');
    }, 2000);
  };

  // AI CONTEXT: ux - Handle age decline
  // Reasoning: Provide clear feedback for underage users
  // Constraints: Must prevent access to age-restricted content
  // Future: Add educational resources for underage users

  const handleDecline = () => {
    setShowDecline(true);
    // Store decline in localStorage
    localStorage.setItem('age_declined', 'true');
  };

  // AI CONTEXT: security - Handle site exit
  // Reasoning: Provide secure exit for underage users
  // Constraints: Must redirect to safe external site
  // Future: Add parental control options

  const handleExit = () => {
    // Close the window or redirect to a safe site
    window.close();
    // Fallback for browsers that don't allow window.close()
    window.location.href = 'https://www.google.com';
  };

  // AI CONTEXT: ux - Show verification success
  // Reasoning: Provide clear confirmation of verification
  // Constraints: Must redirect after confirmation
  // Future: Add verification timestamp and details

  if (isVerified) {
    return (
      <div className="min-h-screen bg-theme-black flex items-center justify-center">
        <div className="text-center">
          <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-theme-silver mb-2">Age Verified!</h2>
          <p className="text-theme-silver-light">Redirecting you to Tolbert Tactical...</p>
        </div>
      </div>
    );
  }

  // AI CONTEXT: ux - Show access denied
  // Reasoning: Provide clear feedback for underage users
  // Constraints: Must prevent access to age-restricted content
  // Future: Add educational resources and parental guidance

  if (showDecline) {
    return (
      <div className="min-h-screen bg-theme-black flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <XCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-theme-silver mb-2">Access Denied</h2>
          <p className="text-theme-silver-light mb-6">
            You must be 21 years or older to access this website. Thank you for your understanding.
          </p>
          <button
            onClick={handleExit}
            className="btn-secondary"
          >
            Exit Site
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-theme-black flex items-center justify-center">
      <div className="max-w-md mx-auto px-4">
        <div className="glass-card-dark text-center">
          {/* Header */}
          <div className="flex justify-center mb-6">
            <Shield className="h-16 w-16 text-custom-orange" />
          </div>
          
          <h1 className="text-3xl font-bold text-theme-silver mb-4">
            Age Verification Required
          </h1>
          
          <div className="mb-6">
            <AlertTriangle className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
            <p className="text-lg text-theme-silver-light mb-4">
              You must be 21 years or older to access this website.
            </p>
            <p className="text-sm text-theme-silver-light">
              By clicking "I am 21 or older", you confirm that you meet the age requirement.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleVerify}
              className="btn-primary w-full text-lg py-4"
            >
              I am 21 or older
            </button>
            
            <button
              onClick={handleDecline}
              className="btn-secondary w-full text-lg py-4"
            >
              I am under 21
            </button>
          </div>

          {/* Legal Notice */}
          <div className="mt-8 pt-6 border-t border-theme-silver/20">
            <p className="text-xs text-theme-silver-light mb-4">
              This website contains content related to tactical gear and firearms. 
              Age verification is required by law.
            </p>
            
            <div className="text-xs text-theme-silver-light space-y-1">
              <p>
                <a 
                  href="/legal#terms" 
                  className="text-custom-orange hover:text-custom-orange/80 underline"
                >
                  Terms of Service
                </a>
                {' • '}
                <a 
                  href="/legal#privacy" 
                  className="text-custom-orange hover:text-custom-orange/80 underline"
                >
                  Privacy Policy
                </a>
                {' • '}
                <a 
                  href="/legal#sweepstakes" 
                  className="text-custom-orange hover:text-custom-orange/80 underline"
                >
                  Sweepstakes Rules
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgeVerification; 